import { useEffect, useRef } from "react";
import type { PinCompareState } from "./usePinCompareState";

export type TooltipMode = "hidden" | "preview" | "interactive" | "pinned";

export interface UseTooltipInteractionModeParams {
  wrapRef: React.RefObject<HTMLElement>;
  getChartUid: () => string;
  pinState: PinCompareState;
  velocityThreshold?: number;
  idleDelayMs?: number;
}

export function useTooltipInteractionMode({
  wrapRef,
  getChartUid,
  pinState,
  velocityThreshold = 0.25,
  idleDelayMs = 150,
}: UseTooltipInteractionModeParams) {
  const modeRef = useRef<TooltipMode>("hidden");

  useEffect(() => {
    const wrapEl = wrapRef.current;
    if (!wrapEl) return;

    const findTooltipRoots = (): HTMLElement[] => {
      const uid = getChartUid();
      if (!uid) return [];
      const roots: HTMLElement[] = [];
      document
        .querySelectorAll(`.remix-tt .enhanced-tooltip[data-chart-uid="${uid}"]`)
        .forEach((node) => {
          const root = (node as HTMLElement).closest(".remix-tt") as
            | HTMLElement
            | null;
          if (root) roots.push(root);
        });
      return roots;
    };

    const stampCurrentMode = () => {
      const mode = modeRef.current;
      findTooltipRoots().forEach((root) => {
        if (root.getAttribute("data-mode") !== mode) {
          root.setAttribute("data-mode", mode);
        }
      });
    };

    const applyMode = (newMode: TooltipMode) => {
      if (modeRef.current !== newMode) {
        modeRef.current = newMode;
      }
      stampCurrentMode();
    };

    if (pinState.isCompareMode) {
      applyMode("preview");
      const raf = requestAnimationFrame(stampCurrentMode);
      return () => cancelAnimationFrame(raf);
    }

    applyMode("preview");
    requestAnimationFrame(stampCurrentMode);

    let lastX = 0;
    let lastY = 0;
    let lastT = 0;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        idleTimer = null;
        applyMode("interactive");
      }, idleDelayMs);
    };

    const isOverTooltip = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      return !!el?.closest?.(".remix-tt");
    };

    const isOverChart = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      return !!el && (el === wrapEl || wrapEl.contains(el));
    };

    const onPointerMove = (e: PointerEvent) => {
      if (isOverTooltip(e.target)) {
        if (idleTimer) {
          clearTimeout(idleTimer);
          idleTimer = null;
        }
        applyMode("interactive");
        lastX = e.clientX;
        lastY = e.clientY;
        lastT = performance.now();
        return;
      }

      if (!isOverChart(e.target)) {
        if (idleTimer) {
          clearTimeout(idleTimer);
          idleTimer = null;
        }
        applyMode("hidden");
        return;
      }

      if (modeRef.current === "hidden") {
        applyMode("preview");
      }

      const now = performance.now();
      const dt = now - lastT;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      lastT = now;

      if (dt > 0) {
        const v = Math.hypot(dx, dy) / dt;
        const dragging = e.buttons !== 0;
        if (dragging || v > velocityThreshold) {
          applyMode("preview");
        }
      }
      scheduleIdle();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (isOverTooltip(e.target)) return;
      if (isOverChart(e.target)) {
        applyMode("preview");
      }
    };

    document.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("pointerdown", onPointerDown, { passive: true });

    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerdown", onPointerDown);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [
    pinState.isCompareMode,
    wrapRef,
    getChartUid,
    idleDelayMs,
    velocityThreshold,
  ]);

}
